import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Mail, MapPin, FlaskConical, Loader2, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      if (response.data.status === 'existing') {
        toast.info('You\'re already on the list');
      } else {
        toast.success('You\'ll be notified of future drops');
      }
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="notify" className="bg-[#1A1A1A] text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Drop Notification Section */}
        <div className="bg-[#2C2C2C] p-8 mb-12 border-l-4 border-[#4A7C29]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#4A7C29] flex items-center justify-center shrink-0">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1 font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">DROP NOTIFICATION SYSTEM</h3>
                <p className="text-[#6B6B6B] text-sm font-['IBM_Plex_Sans']">
                  Register for automated alerts. No promotional content. Drops only.
                </p>
              </div>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-3">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@institution.org"
                className="flex-1 bg-[#1A1A1A] border-2 border-[#404040] rounded-none text-white placeholder:text-[#6B6B6B] font-['IBM_Plex_Sans'] focus:border-[#4A7C29]"
                data-testid="newsletter-email"
              />
              <Button 
                type="submit"
                disabled={loading}
                className="bg-[#4A7C29] hover:bg-[#3D6822] text-white font-semibold px-6 rounded-none font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider text-xs"
                data-testid="newsletter-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'REGISTER'}
              </Button>
            </form>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#4A7C29] flex items-center justify-center">
                <FlaskConical className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-base font-semibold font-['IBM_Plex_Sans_Condensed'] tracking-[0.15em] uppercase">TASTYJELLY</span>
                <span className="text-[9px] text-[#6B6B6B] block tracking-[0.2em] font-['IBM_Plex_Sans_Condensed'] uppercase">GLOBAL INDUSTRIES</span>
              </div>
            </div>
            <p className="text-[#6B6B6B] mb-6 max-w-md text-sm leading-relaxed font-['IBM_Plex_Sans']">
              Applying the authority of a global research institution to problems that could have been solved without it. Small batch ultralight gear. Limited quantities.
            </p>
            <div className="flex gap-2">
              <a href="#" className="p-2 bg-[#2C2C2C] hover:bg-[#404040] transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="#" className="p-2 bg-[#2C2C2C] hover:bg-[#404040] transition-colors" aria-label="Twitter">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="#" className="p-2 bg-[#2C2C2C] hover:bg-[#404040] transition-colors" aria-label="Email">
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Divisions */}
          <div>
            <h4 className="font-semibold text-[10px] mb-4 tracking-[0.2em] font-['IBM_Plex_Sans_Condensed'] uppercase text-[#4A7C29]">DIVISIONS</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/products" className="text-[#6B6B6B] hover:text-white transition-colors text-sm font-['IBM_Plex_Sans']">
                  Product Releases
                </Link>
              </li>
              <li>
                <Link to="/brand" className="text-[#6B6B6B] hover:text-white transition-colors text-sm font-['IBM_Plex_Sans']">
                  Brand Assets
                </Link>
              </li>
            </ul>
          </div>

          {/* Corporate */}
          <div>
            <h4 className="font-semibold text-[10px] mb-4 tracking-[0.2em] font-['IBM_Plex_Sans_Condensed'] uppercase text-[#4A7C29]">CORPORATE</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-[#6B6B6B] hover:text-white transition-colors text-sm font-['IBM_Plex_Sans']">
                  About TJGI
                </Link>
              </li>
              <li>
                <a href="#" className="text-[#6B6B6B] hover:text-white transition-colors text-sm font-['IBM_Plex_Sans']">
                  Contact
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-[#2C2C2C] pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2 text-[#5A5A5A] text-xs font-['IBM_Plex_Sans']">
            <MapPin className="w-3 h-3" />
            <span>Research conducted at undisclosed location</span>
          </div>
          <p className="text-[#5A5A5A] text-xs font-['IBM_Plex_Sans']">
            © {new Date().getFullYear()} TastyJelly Global Industries — All Rights Reserved
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
